import React from 'react'
import { SignNowEmbedFrame } from '../../lib/components/SignNowEmbedFrame'

type InvestV2TermsheetInlineProps = {
  signed: boolean
  isSigningPending: boolean
  isLoading: boolean
  requiresResign?: boolean
  resignMessage?: string
  downloadLink?: string | null
  /** Fresh SignNow URL — render as iframe on this step; never open/redirect */
  signingLink?: string | null
  onSign: () => void | Promise<void>
  onReopenSigning?: () => void
  onDownloadSignedTermsheet?: () => void
  /** Close iframe when SignNow redirects to /invest/signing-complete */
  onSigningComplete?: () => void
}

const InvestV2TermsheetInline = ({
  signed,
  isSigningPending,
  isLoading,
  requiresResign = false,
  resignMessage,
  downloadLink,
  signingLink = null,
  onSign,
  onReopenSigning,
  onDownloadSignedTermsheet,
  onSigningComplete,
}: InvestV2TermsheetInlineProps) => {
  const isSigningOpen = Boolean(signingLink && !signed)

  const signButtonLabel = isLoading
    ? 'Preparing document…'
    : isSigningPending
    ? 'Processing signature...'
    : 'View and sign the term sheet'

  const defaultResignMessage =
    'Your investment amount changed or the previous signature was cancelled. Please sign the term sheet again.'

  const leadText = signed
    ? 'Your term sheet has been signed electronically.'
    : isSigningOpen
    ? 'Sign the term sheet below. We will detect completion automatically — stay on this page.'
    : isSigningPending
    ? 'Processing signature...'
    : requiresResign
    ? resignMessage ?? defaultResignMessage
    : 'Sign the term sheet here without leaving this page.'

  return (
    <div
      className='invest-v2__ie-inline invest-v2__termsheet-inline'
      data-testid='invest-v2-termsheet-inline'
    >
      <div className='invest-v2__termsheet-header'>
        <h2 className='invest-v2__risk-title invest-v2__termsheet-title'>
          <span className='invest-v2__risk-icon' aria-hidden>
            📄
          </span>
          Term Sheet
        </h2>
        {signed && (
          <span
            className='invest-v2__termsheet-badge invest-v2__termsheet-badge--signed'
            data-testid='invest-v2-termsheet-signed-badge'
          >
            Signed
          </span>
        )}
        {!signed && (isSigningPending || isSigningOpen) && (
          <span
            className='invest-v2__termsheet-badge invest-v2__termsheet-badge--pending'
            data-testid='invest-v2-termsheet-pending-badge'
          >
            {isSigningOpen ? 'Signing' : 'Processing signature'}
          </span>
        )}
        {!signed && requiresResign && !isSigningPending && !isSigningOpen && (
          <span
            className='invest-v2__termsheet-badge invest-v2__termsheet-badge--pending'
            data-testid='invest-v2-termsheet-resign-badge'
          >
            Resign required
          </span>
        )}
      </div>

      <p className='invest-v2__termsheet-lead' data-testid='invest-v2-termsheet-lead'>
        {leadText}
      </p>

      {signed && downloadLink && onDownloadSignedTermsheet && (
        <div className='invest-v2__termsheet-actions'>
          <button
            type='button'
            className='invest-v2__link-btn invest-v2__termsheet-download'
            onClick={onDownloadSignedTermsheet}
            data-testid='invest-v2-termsheet-download'
          >
            Download signed termsheet
          </button>
        </div>
      )}

      {isSigningOpen && signingLink && (
        <div className='invest-v2__termsheet-embed' data-testid='invest-v2-termsheet-embed'>
          <SignNowEmbedFrame
            signingLink={signingLink}
            title='Sign termsheet'
            testId='invest-v2-termsheet-signnow'
            className='invest-v2__termsheet-embed-frame'
            onSigningComplete={onSigningComplete}
          />
          {onReopenSigning && (
            <button
              type='button'
              className='invest-v2__link-btn invest-v2__termsheet-reopen'
              onClick={onReopenSigning}
              data-testid='invest-v2-termsheet-reopen'
            >
              Get a new signing link
            </button>
          )}
        </div>
      )}

      {!signed && !isSigningOpen && (
        <div className='invest-v2__termsheet-actions'>
          <div className='invest-v2__termsheet-sign-block'>
            <button
              type='button'
              className='invest-v2__termsheet-sign-btn'
              onClick={() => void onSign()}
              disabled={isLoading || isSigningPending}
              data-testid='invest-v2-termsheet-sign'
            >
              {signButtonLabel}
            </button>
            {isSigningPending && onReopenSigning && (
              <button
                type='button'
                className='invest-v2__link-btn invest-v2__termsheet-reopen'
                onClick={onReopenSigning}
                data-testid='invest-v2-termsheet-reopen'
              >
                Reopen signing
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export { InvestV2TermsheetInline }
